import styled from 'styled-components';
import { Theme } from '../../pages/Styles/Theme';
import { Progress } from 'antd';

export const SidebarWrapper = styled.div`
  @media (max-width: 991px) {
    display: none;
  }
  /* App.js offsets the content by sidebarWidth (210) and .appPageContainer
     adds its own 20px gutter, so page content actually begins at 230. The
     rail used to stop at 220, which left a 10px strip of page between the
     rail's right edge - where the menu's scroll indicator sits - and the
     first card, and the indicator read as floating in space (user
     2026-09-24). The rail now ends exactly where content begins when it is
     expanded: 210 + 20. Content does not move; the rail is fixed and only
     covers the gutter it was already overlapping. Collapsed keeps its 60px,
     since its icons are centred on that width and the indicator complaint
     was about the expanded rail. */
  width: ${props => (props.$collapsed ? '60px' : '230px')};
  /* How far every block stops short of the rail's right edge. Expanded, the
     edge IS where the page panel begins, so anything running to it reads as
     glued to the page (user 2026-09-28: the profile card and the CrowdVox
     badge "look like they will overlap"). 6px = the menu items' inset.
     Collapsed: 0, so the icons stay centred on the 60px rail. */
  --rail-inset: ${props => (props.$collapsed ? '0px' : '6px')};
  height: 100vh;
  background: ${({ theme }) => theme.settings.whiteColor};
  border-right: none;
  position: fixed;
  left: 0;
  top: 0;
  padding: 0px 0 12px 12px;
  display: flex;
  flex-direction: column;
  /* The MENU scrolls (see MenuWrapper), not the whole rail, so the profile,
     powered-by and version blocks stay pinned at the bottom on short
     windows instead of scrolling out of sight behind an invisible
     scrollbar (user 2026-09-10). */
  overflow: hidden;
  transition: width 0.3s ease;
  z-index: 1000;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-track {
    /* background: ${({ theme }) => theme.settings.bodyBg}; */
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    /* background: ${({ theme }) => theme.settings.borderColor}; */
    background: transparent;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    /* background: ${({ theme }) => theme.settings.bodyBg}; */
    background: transparent;
  }
`;

export const SidebarHeader = styled.div`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.settings.whiteColor};
  width: 100%;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 0px 0px 0px;
  margin-bottom: 20px;
  ${props => props.$collapsed && 'position: relative; min-height: 36px;'}
`;

export const ThemeLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${props => (props.$collapsed ? 'center' : 'flex-start')};
  flex-shrink: 0;
  ${props =>
    props.$collapsed &&
    'position: absolute; inset: 0; z-index: 0; margin-top: 10px;'}
  img {
    height: ${props => (props.$collapsed ? '18px' : '22px')};
    width: ${props => (props.$collapsed ? 'auto' : 'auto')};
    max-width: ${props => (props.$collapsed ? '32px' : 'none')};
    object-fit: contain;
    display: block;
    transition:
      height 0.2s ease,
      max-width 0.2s ease;
  }
`;

export const ToggleSwitchWrap = styled.div`
  opacity: ${props => (props.$collapsed && !props.$isHovered ? 0 : 1)};
  visibility: ${props =>
    props.$collapsed && !props.$isHovered ? 'hidden' : 'visible'};
  pointer-events: ${props =>
    props.$collapsed && !props.$isHovered ? 'none' : 'auto'};
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;
  ${props =>
    props.$collapsed &&
    `
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 10px;
  `}
`;

export const ToggleSwitch = styled.div`
  cursor: pointer;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: ${props =>
    props.$collapsed ? ({ theme }) => theme.settings.bodyBg : 'transparent'};
  box-shadow: ${props =>
    props.$collapsed ? '0 1px 2px rgba(0,0,0,0.06)' : 'none'};
  transition:
    transform 0.3s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;
  &:hover {
    background: ${props =>
      props.$collapsed ? ({ theme }) => theme.settings.bodyBg : 'transparent'};
    box-shadow: ${props =>
      props.$collapsed ? '0 2px 6px rgba(0,0,0,0.1)' : 'none'};
  }
  img {
    transition: transform 0.3s ease;
    transform: ${props =>
      props.$collapsed ? 'rotate(0deg)' : 'rotate(180deg)'};
  }
`;

export const RightArrowIcon = styled.div`
  color: #6D6A67;
  font-size: 12px;
`;

export const ProfileCard = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: ${({ theme }) => theme.settings.bodyBg};
  border-radius: 10px;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  @media only screen and (max-width: 991.99px) {
    border-radius: 0px;
    margin-bottom: 0px;
    border-bottom: 1px solid ${({ theme }) => theme.settings.borderColor};
  }
  &:hover {
    transform: scale(0.95);
  }
`;

export const ProfileAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 10px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ProfileName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.settings.primary};
  line-height: 1.4;
`;

export const ProfileRole = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.settings.textColor};
  line-height: 1.4;
`;

/* The menu and its own scroll indicator. macOS/Chrome draws an OVERLAY
   scrollbar here - zero layout width, painted on top of the menu items and
   only while scrolling - and it ignores every ::-webkit-scrollbar rule as
   soon as scrollbar-width/color are set. So the native bar is hidden and
   Sidebar.js draws the thumb below instead (user 2026-09-24: always show a
   very thin line at the rail's edge, widen it slightly on hover). */
export const MenuScrollArea = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  margin-bottom: 8px;
  /* When the menu runs past the fold, the last visible item used to be cut
     straight across right above the profile card and read as overlapping
     it (user 2026-09-28). A short fade says "more below" instead. It stops
     at --rail-inset so the scroll indicator on the edge stays crisp, and is
     white-on-white (invisible) whenever nothing is under it - the menu's
     bottom padding keeps the last item clear of it at the end of a scroll. */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: var(--rail-inset, 0px);
    bottom: 0;
    height: 20px;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), ${({ theme }) => theme.settings.whiteColor});
    pointer-events: none;
  }
`;

export const MenuWrapper = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* Items stop 6px short of the rail edge so the indicator never sits on
     top of a hover background. Not when collapsed: there the icons are
     centred on the rail's full width, same as the logo and the avatar, and
     a right padding would pull them 3px off that centre line (user
     2026-09-24). */
  padding-right: ${props => (props.$collapsed ? '0' : '6px')};
  /* Room under the last item so, scrolled to the end, it sits above the
     MenuScrollArea fade rather than inside it (8px item margin + 16 > 20). */
  padding-bottom: 16px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
`;

/* Track: a hairline the full height of the scrollable menu, so it is
   obvious the menu scrolls even before the pointer arrives. Sits flush on
   the rail's right edge (the rail has no right padding). */
export const MenuScrollTrack = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 2px;
  border-radius: 1px;
  background: ${({ theme }) => theme.settings.ctaBorderColor};
  transition: width 0.15s ease;
  pointer-events: none;
  /* Above the MenuScrollArea bottom fade (a later-painted ::after). */
  z-index: 1;
  ${MenuScrollArea}:hover & {
    width: 4px;
  }
`;

export const MenuScrollThumb = styled.div`
  position: absolute;
  right: 0;
  width: 2px;
  border-radius: 1px;
  background: ${({ theme }) => theme.settings.borderColor};
  transition: width 0.15s ease, background 0.15s ease;
  pointer-events: none;
  z-index: 2;
  ${MenuScrollArea}:hover & {
    width: 4px;
    background: ${({ theme }) => theme.settings.borderActiveColor};
  }
`;


export const MenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  background: ${props =>
    props.$active ? Theme.settings.whiteColor : 'transparent'};
  border: 1px solid
    ${props => (props.$active ? Theme.settings.borderColor : 'transparent')};

  color: ${props =>
    props.$active ? Theme.settings.primary : Theme.settings.secondary};
  transition: all 0.3s ease;
  &:hover {
    transform: scale(0.95);
  }
`;

export const MenuIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
  img {
    width: 20px;
    height: 20px;
    filter: ${props =>
      props.$active
        ? 'brightness(0) saturate(100%) invert(12%) sepia(8%) saturate(818%) hue-rotate(349deg) brightness(87%) contrast(85%)'
        : 'brightness(0) saturate(100%) invert(30%) sepia(7%) saturate(1811%) hue-rotate(187deg) brightness(93%) contrast(88%)'};
  }
`;

export const MenuLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MenuBadge = styled.span`
  background: ${({ theme }) => theme.settings.primary};
  color: ${({ theme }) => theme.settings.whiteColor};
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 8px;
  line-height: 1.4;
`;

// New collapsed menu components
export const CollapsedMenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: 0 auto 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  background: ${props => (props.$active ? '#F7F5F0' : 'transparent')};
  /* &:hover {
    background: #F7F5F0;
  } */
`;

export const CollapsedMenuIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  img {
    width: 20px;
    height: 20px;
    filter: ${props =>
      props.$active
        ? 'brightness(0) saturate(100%) invert(12%) sepia(8%) saturate(818%) hue-rotate(349deg) brightness(87%) contrast(85%)'
        : 'brightness(0) saturate(100%) invert(30%) sepia(7%) saturate(1811%) hue-rotate(187deg) brightness(93%) contrast(88%)'};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const CollapsedBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  background: ${({ theme }) => theme.settings.primary};
  color: ${({ theme }) => theme.settings.whiteColor};
  font-size: 8px;
  font-weight: 600;
  padding: 1px 4px;
  border-radius: 8px;
  line-height: 1.2;
  min-width: 12px;
  text-align: center;
`;

export const ProfileCompletion = styled.div`
  background: ${({ theme }) => theme.settings.bodyBg};
  border-radius: 12px;
  padding: 20px 16px;
  margin-bottom: 16px;
  text-align: center;
  position: relative;
  transition: all 0.3s ease;
  @media only screen and (max-width: 991.99px) {
    border-radius: 0px;
  }
  &:hover {
    transform: scale(0.95);
  }
`;
export const ProgressText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.settings.primary};
`;

export const CompletionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.settings.primary};
  margin-bottom: 8px;
  line-height: 1.4;
  margin-top: 8px;
`;

export const CompletionSubtitle = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.settings.textColor};
  margin-bottom: 16px;
  line-height: 1.5;
`;

export const ProfileButton = styled.button`
  width: 100%;
  padding: 8px 16px;
  background: ${({ theme }) => theme.settings.whiteColor};
  border: 1px solid ${({ theme }) => theme.settings.borderColor};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.settings.primary};
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: ${({ theme }) => theme.settings.primary};
    color: ${({ theme }) => theme.settings.whiteColor};
    border-color: ${({ theme }) => theme.settings.primary};
  }
`;

export const HelpCenter = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: ${({ theme }) => theme.settings.bodyBg};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    transform: scale(0.95);
  }
`;

export const HelpIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
`;

export const HelpContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const HelpTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.settings.primary};
  line-height: 1.4;
`;

export const HelpSubtitle = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.settings.textColor};
  line-height: 1.4;
`;
export const ProgressSidebar = styled(Progress)`
  width: 100%;
  position: relative;
  .ant-progress-text {
    font-family: ${({ theme }) => theme.fonts.inter};
    font-size: 16px !important;
    font-weight: 600 !important;
    color: ${({ theme }) => theme.settings.primary} !important;
    line-height: 1.4 !important;
  }
`;

export const LogoutTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.settings.primary};
  line-height: 1.4;
`;

export const LogoutButton = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: ${({ theme }) => theme.settings.bodyBg};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 8px;
  &:hover {
    transform: scale(0.95);
  }
`;

export const LogoutIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
`;

export const LogoutContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const BottomSection = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
`;

// ChatGPT-style Bottom Profile Menu
export const PoweredByWrapper = styled.div`
  padding: 6px 0 4px;
  /* Same right inset as the menu items (see --rail-inset on SidebarWrapper). */
  margin-right: var(--rail-inset, 0px);
`;

export const PoweredByBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: none;
  background: #f0f1f3;

  span {
    font-size: 14px;
    font-weight: 500;
    color: #5d5e63;
    white-space: nowrap;
    font-family: 'Inter', sans-serif;
    line-height: 1;
  }

  img {
    height: 22px;
    object-fit: contain;
  }
`;

export const VersionWrapper = styled.div`
  padding: 0 12px 4px 12px;
  margin-right: var(--rail-inset, 0px);
`;

export const VersionDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.settings.borderColor};
  opacity: 0.4;
  margin-bottom: 8px;
`;

export const VersionText = styled.div`
  font-size: 10px;
  font-weight: 500;
  color: ${({ theme }) => theme.settings.textColor};
  opacity: 0.35;
  text-align: center;
  letter-spacing: 0.3px;
`;

export const BottomProfileWrapper = styled.div`
  margin-top: auto;
  position: relative;
  /* The pinned footer (profile, CrowdVox badge, version) is its own zone: a
     hairline divides it from the scrolling menu and it keeps the menu's
     right inset, so neither the hover fill nor the badge touches the page
     panel (user 2026-09-28). Collapsed: inset 0, line spans the 48px icon
     column and stays centred with the icons. */
  margin-right: var(--rail-inset, 0px);
  padding-top: 8px;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: ${({ theme }) => theme.settings.borderColorLight || '#f0f0f0'};
  }
`;

export const BottomProfileButton = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props =>
    props.$isOpen ? ({ theme }) => theme.settings.bodyBg : 'transparent'};

  &:hover {
    background: ${({ theme }) => theme.settings.bodyBg};
  }
`;

export const BottomProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
  margin-left: 10px;
  display: flex;
  align-items: center;
`;

export const BottomProfileName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.settings.primary};
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const BottomProfileRole = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.settings.textColor};
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ProfileDropdownMenu = styled.div`
  position: absolute;
  /* BottomProfileWrapper's 8px padding-top (above its divider) would push the
     menu 8px further from the profile button; pull it back to the old gap.
     HeaderTop.js overrides bottom inline, so it is unaffected. */
  bottom: calc(100% - 8px);
  left: 0;
  right: 0;
  margin-bottom: 8px;
  background: ${({ theme }) => theme.settings.whiteColor};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid ${({ theme }) => theme.settings.borderColor};
  overflow: hidden;
  z-index: 1001;
  opacity: ${props => (props.$isOpen ? 1 : 0)};
  visibility: ${props => (props.$isOpen ? 'visible' : 'hidden')};
  transform: ${props => (props.$isOpen ? 'translateY(0)' : 'translateY(10px)')};
  transition: all 0.2s ease;
`;

export const ProfileDropdownItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  color: ${({ theme }) => theme.settings.primary};
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: ${({ theme }) => theme.settings.bodyBg};
  }

  img,
  svg {
    width: 18px;
    height: 18px;
    margin-right: 12px;
    filter: ${({ theme }) => theme.settings.imgFilterColor};
  }

  &.danger {
    color: #e65b5b;

    img,
    svg {
      filter: brightness(0) saturate(100%) invert(47%) sepia(74%)
        saturate(1143%) hue-rotate(322deg) brightness(93%) contrast(92%);
    }
  }
`;

export const ProfileDropdownDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.settings.borderColor};
  margin: 4px 0;
`;

export const CollapsedBottomProfile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: 0 auto 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    background: #F7F5F0;
  }
`;
